import { ClassificationResult } from '@jump-tech-frontend/domain';

export type DetectedBox = NonNullable<ClassificationResult['boundingBoxes']>[number];

export interface DetectedBoxVm {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly colour: string;
  readonly label: string;
}

const BOX_COLOURS: [string, string][] = [
  ['fuse', '#007bff'],
  ['consumer', '#28a745']
];

const DEFAULT_BOX_COLOUR = '#6c757d';

function normaliseLabel(value: string): string {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function primaryBoxes(boxes: readonly DetectedBox[], equipmentType: string | undefined): DetectedBox[] {
  if (!equipmentType) {
    return [...boxes];
  }
  const primaryKey = normaliseLabel(equipmentType);
  return boxes.filter(box => normaliseLabel(box.label) === primaryKey);
}

export function boxColour(label: string): string {
  const lowerLabel = (label || '').toLowerCase();
  return BOX_COLOURS.find(([fragment]) => lowerLabel.includes(fragment))?.[1] ?? DEFAULT_BOX_COLOUR;
}

export function formatBoxLabel(rawLabel: string): string {
  if (!rawLabel) {
    return '';
  }
  return rawLabel
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function confidencePercent(confidence: number): number {
  return confidence <= 1 ? confidence * 100 : confidence;
}

export function boxDisplayLabel(box: DetectedBox): string {
  return `${formatBoxLabel(box.label)} (${confidencePercent(box.confidence).toFixed(1)}%)`;
}

export function detectedBoxesOf(result: ClassificationResult | null | undefined): DetectedBoxVm[] {
  const sourceWidth = result?.sourceWidth || result?.imageWidth || 0;
  const sourceHeight = result?.sourceHeight || result?.imageHeight || 0;
  if (!result?.boundingBoxes?.length || sourceWidth <= 0 || sourceHeight <= 0) {
    return [];
  }

  return primaryBoxes(result.boundingBoxes, result.equipmentType).map(box => ({
    left: (box.x1 / sourceWidth) * 100,
    top: (box.y1 / sourceHeight) * 100,
    width: (Math.max(0, box.x2 - box.x1) / sourceWidth) * 100,
    height: (Math.max(0, box.y2 - box.y1) / sourceHeight) * 100,
    colour: boxColour(box.label),
    label: boxDisplayLabel(box)
  }));
}
